import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { setStationToken, whoami } from "../lib/auth";

export default function SetStationToken() {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = token.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    setStationToken(trimmed);
    const user = await whoami();
    setBusy(false);
    if (!user) {
      setStationToken("");
      setError("Token rejected. Make sure it was issued by an owner and hasn't been revoked.");
      return;
    }
    navigate("/", { replace: true });
  }

  return (
    <main style={styles.shell}>
      <div style={styles.card}>
        <h1 style={styles.title}>Tablet setup</h1>
        <p style={styles.subtitle}>
          Paste the tablet token an owner gave you. You only do this once per
          tablet.
        </p>

        <form onSubmit={onSubmit} style={styles.form}>
          <label style={styles.label}>Tablet token</label>
          <textarea
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="64-character hex string"
            style={styles.textarea}
            rows={3}
            disabled={busy}
            autoFocus
          />
          <button type="submit" style={styles.primaryBtn} disabled={busy || !token.trim()}>
            {busy ? "Verifying…" : "Activate tablet"}
          </button>
        </form>

        {error && <p style={styles.error}>{error}</p>}

        <hr style={styles.divider} />
        <p style={styles.footnote}>
          Not a tablet?{" "}
          <Link to="/login" style={styles.footnoteLink}>
            Sign in with your phone or email →
          </Link>
        </p>
      </div>
    </main>
  );
}

const styles: Record<string, React.CSSProperties> = {
  shell: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  card: {
    width: "100%",
    maxWidth: 420,
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: 24,
  },
  title: {
    fontFamily: "Georgia, serif",
    margin: "0 0 4px",
    fontSize: 28,
    color: "var(--gold)",
    letterSpacing: -0.5,
  },
  subtitle: {
    margin: "0 0 20px",
    color: "var(--muted)",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.4,
  },
  form: { display: "grid", gap: 10 },
  label: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "var(--muted)",
    fontWeight: 800,
  },
  textarea: {
    minHeight: 80,
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    padding: 12,
    outline: "none",
    fontSize: 16,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    resize: "vertical",
  },
  primaryBtn: {
    height: 44,
    borderRadius: 12,
    border: "1px solid var(--gold)",
    background: "var(--gold)",
    color: "#0D0B08",
    fontSize: 14,
    fontWeight: 800,
    marginTop: 4,
  },
  error: {
    marginTop: 12,
    padding: 10,
    background: "rgba(224, 80, 64, 0.12)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    borderRadius: 10,
    color: "#FF8A7A",
    fontSize: 13,
    fontWeight: 600,
  },
  divider: {
    margin: "24px 0 16px",
    border: 0,
    borderTop: "1px solid var(--border)",
  },
  footnote: {
    margin: 0,
    color: "var(--muted)",
    fontSize: 12,
    textAlign: "center",
  },
  footnoteLink: { color: "var(--gold)", fontWeight: 700 },
};
